import { Accordion, Code, Group, List, Paper, Stack, Text } from '@mantine/core'

import type { TraceStep } from '../types'
import { formatDateTime } from '../utils/format'
import { sortTraceStepsByExecution } from '../utils/sort-trace-steps'
import { parseHubspotEventMetadata, isHubspotFailureEvent } from '../utils/hubspot-event-details'
import { ActionBadge, PhaseBadge } from './StatusBadge'
import { HubspotApiDetails } from './HubspotApiDetails'
import { MergeDecisionSummary } from './MergeDecisionSummary'
import { extractMergePlan, parseMergeMessage } from '../utils/merge-plan'

type Props = {
  steps: TraceStep[]
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

const ContactScoredSummary = ({ metadata }: { metadata: Record<string, unknown> }) => {
  const confidence = metadata.confidence
  const reasons = stringList(metadata.reasons)
  const scoreBreakdown = stringList(metadata.scoreBreakdown)
  const linkedin = typeof metadata.linkedin === 'string' ? metadata.linkedin : null
  const contactLinkedins =
    metadata.contactLinkedins && typeof metadata.contactLinkedins === 'object'
      ? (metadata.contactLinkedins as Record<string, string | null>)
      : null

  return (
    <Stack gap={6} mb='xs'>
      {confidence != null && (
        <Text size='sm'>
          <Text span fw={600}>
            Confidence:{' '}
          </Text>
          {String(confidence)}
        </Text>
      )}
      {contactLinkedins && Object.keys(contactLinkedins).length > 0 ? (
        <Stack gap={2}>
          {Object.entries(contactLinkedins).map(([contactId, url]) => (
            <Text key={contactId} size='sm'>
              <Text span fw={600}>
                {contactId}:{' '}
              </Text>
              {url ?? '—'}
            </Text>
          ))}
        </Stack>
      ) : linkedin ? (
        <Text size='sm'>
          <Text span fw={600}>
            LinkedIn:{' '}
          </Text>
          {linkedin}
        </Text>
      ) : null}
      {scoreBreakdown.length > 0 && (
        <Text size='sm'>
          <Text span fw={600}>
            Score:{' '}
          </Text>
          {scoreBreakdown.join(' ')}
        </Text>
      )}
      {reasons.length > 0 && (
        <List size='sm' spacing={2}>
          {reasons.map((reason) => (
            <List.Item key={reason}>{reason}</List.Item>
          ))}
        </List>
      )}
    </Stack>
  )
}

const GroupDecidedSummary = ({
  metadata,
  message,
}: {
  metadata: Record<string, unknown>
  message?: string | null
}) => {
  const plan =
    extractMergePlan((metadata.plan as Record<string, unknown> | undefined) ?? metadata) ??
    (message ? parseMergeMessage(message) : null)

  if (!plan?.targetContactId && !plan?.mergeContactId) {
    return null
  }

  return <MergeDecisionSummary plan={plan} message={message} />
}

const EXECUTE_HUBSPOT_EVENTS = new Set([
  'hubspot_completed',
  'hubspot_failed',
  'company_merged',
  'company_deleted',
  'company_domain_updated',
  'contact_merged',
  'contact_kept',
  'review_task_created',
  'hubspot_batch_flushed',
])

export const TraceTimeline = ({ steps }: Props) => {
  const orderedSteps = sortTraceStepsByExecution(steps)

  return (
    <Stack gap='md'>
      {orderedSteps.map((step, index) => {
        const order = step.stepOrder ?? index + 1
        const isHubspot =
          step.phase === 'execute' &&
          (EXECUTE_HUBSPOT_EVENTS.has(step.event) ||
            isHubspotFailureEvent(step.event, step.metadata))
        const isSuccess =
          step.event === 'hubspot_completed' ||
          [
            'company_merged',
            'company_deleted',
            'company_domain_updated',
            'review_task_created',
          ].includes(step.event)
        const isFailure = isHubspotFailureEvent(step.event, step.metadata)
        const hubspotDetails =
          isHubspot && (isSuccess || isFailure)
            ? parseHubspotEventMetadata(step.metadata, {
                companyId: step.companyId,
                companyName: step.companyName,
                domain: step.domain,
                action: step.action,
              })
            : null

        return (
          <Paper
            key={step.eventId}
            withBorder
            p='md'
            radius='md'
            style={{
              borderColor: isSuccess ? '#40c057' : isFailure ? '#fa5252' : undefined,
              borderWidth: isHubspot ? 2 : 1,
            }}
          >
            <Group justify='space-between' mb='xs' wrap='wrap'>
              <Group gap='sm'>
                <Text fw={700} c='blue'>
                  {order}
                </Text>
                <PhaseBadge phase={step.phase} />
                <Text fw={600}>{step.event}</Text>
                {step.action && <ActionBadge action={step.action} />}
              </Group>
              <Text size='xs' c='dimmed'>
                {formatDateTime(step.ts)}
              </Text>
            </Group>

            {(step.rule || step.status) && (
              <Group gap='xs' mb='xs'>
                {step.rule && (
                  <Text size='sm' c='dimmed'>
                    Rule: {step.rule}
                  </Text>
                )}
                {step.status && (
                  <Text size='sm' c='dimmed'>
                    Status: {step.status}
                  </Text>
                )}
              </Group>
            )}

            {hubspotDetails ? (
              <HubspotApiDetails
                details={hubspotDetails}
                message={step.message}
                variant={isFailure ? 'failure' : isSuccess ? 'success' : 'neutral'}
                action={step.action}
              />
            ) : (
              <>
                {step.event === 'contact_scored' && step.metadata ? (
                  <ContactScoredSummary metadata={step.metadata} />
                ) : null}
                {step.event === 'group_decided' && step.metadata ? (
                  <GroupDecidedSummary metadata={step.metadata} message={step.message} />
                ) : null}
                {step.event === 'contact_merged' && step.metadata
                  ? (() => {
                      const plan =
                        extractMergePlan(step.metadata) ??
                        (step.message ? parseMergeMessage(step.message) : null)
                      return plan ? (
                        <MergeDecisionSummary plan={plan} message={step.message} />
                      ) : null
                    })()
                  : null}
                {step.message &&
                step.event !== 'contact_scored' &&
                step.event !== 'contact_merged' &&
                !(
                  step.event === 'group_decided' &&
                  extractMergePlan(
                    (step.metadata?.plan as Record<string, unknown> | undefined) ??
                      step.metadata ??
                      {}
                  )
                ) ? (
                  <Text size='sm' mb='xs'>
                    {step.message}
                  </Text>
                ) : null}
                {step.metadata &&
                Object.keys(step.metadata).length > 0 &&
                step.event !== 'contact_scored' &&
                step.event !== 'contact_merged' &&
                step.event !== 'group_decided' ? (
                  <Accordion variant='contained' chevronPosition='left'>
                    <Accordion.Item value='metadata'>
                      <Accordion.Control>Metadata</Accordion.Control>
                      <Accordion.Panel>
                        <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                          {JSON.stringify(step.metadata, null, 2)}
                        </Code>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                ) : null}
              </>
            )}
          </Paper>
        )
      })}
    </Stack>
  )
}
